import React from 'react';
import { useLocale } from 'locale';
import { EnhancedPromotionPlanInfo } from '../private-bulletin-select-promotion-plan-modal/types';
import { getPromotionPlanPriceText } from 'components/private-bulletin-promotion/helpers';
import styled from '@emotion/styled';
import CheckIcon from 'assets/svg/circle-check.svg';
import { Text, SmallText } from 'ds/components/typography';
import { Button } from 'ds/components/button';

const OfferCardRoot = styled.div(({ theme }) => ({
  width: '100%',
  maxWidth: 520,
  border: `2px solid ${theme.colors.brand.madlanGreen}`,
  borderRadius: theme.borderRadius.large,
  padding: `${theme.spacing(3)} ${theme.spacing(3)} ${theme.spacing(2)}`,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  gap: theme.spacing(3),
}));

const OfferCardInner = styled.div(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
  width: '100%',
}));

const OfferTitleBlock = styled.div(() => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
}));

const OfferTitle = styled(Text.withComponent('div'))(({ theme }) => ({
  fontWeight: 700,
  fontSize: 25,
  lineHeight: '32px',
  color: theme.colors.neutrals.grey1,
}));

const OfferDivider = styled.div(({ theme }) => ({
  width: '100%',
  height: 1,
  backgroundColor: theme.colors.neutrals.grey8,
}));

const CTARow = styled.div(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  gap: theme.spacing(1.5),
  [theme.media.createObjectMediaQuery({ from: 3 })]: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
}));

const PriceBlock = styled.div(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  fontSize: 13,
  lineHeight: '20px',
  color: theme.colors.neutrals.grey2,
  '& strong': {
    fontWeight: 700,
    fontSize: 25,
    lineHeight: '32px',
    color: theme.colors.neutrals.grey1,
  },
}));

const CTAButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.colors.brand.madlanGreen,
  color: theme.colors.neutrals.white,
  borderColor: theme.colors.brand.madlanGreen,
  '&:hover': {
    backgroundColor: theme.colors.brand.madlanGreen,
    borderColor: theme.colors.brand.madlanGreen,
  },
  '&:active': {
    backgroundColor: theme.colors.brand.madlanGreen,
    borderColor: theme.colors.brand.madlanGreen,
  },
}));

const FeatureList = styled.div(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

const FeatureText = styled(SmallText)(({ theme }) => ({
  color: theme.colors.neutrals.grey1,
}));

const FeatureRow = styled.div<{ disabled: boolean; }>(({ theme, disabled }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: theme.spacing(1.5),
  ...(disabled ? {
    ' > div': {
      color: theme.colors.neutrals.grey6,
    },
    'svg': {
      filter: 'grayscale(100%)',
      path: {
        display: 'none',
      },
    },
  } : {}),
}));

const CheckIconWrapper = styled.div(({ theme }) => ({
  width: '20px',
  height: '20px',
  minWidth: '20px',
  borderRadius: theme.borderRadius.round,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

interface OfferCardProps {
  plan: EnhancedPromotionPlanInfo;
  onPlanSelect: (plan: EnhancedPromotionPlanInfo) => void;
}

export const OfferCard: React.FC<OfferCardProps> = (props) => {
  const { plan, onPlanSelect } = props;
  const { t } = useLocale();

  const handleSubmit = () => {
    onPlanSelect(plan);
  };

  return (
    <OfferCardRoot>
      <OfferCardInner>
        <OfferTitleBlock>
          <OfferTitle>{plan.planTitle}</OfferTitle>
        </OfferTitleBlock>
        <FeatureList>
          {plan.features.map(feature => (
            <FeatureRow
              key={feature.id}
              disabled={feature.disabled}
            >
              <CheckIconWrapper><CheckIcon /></CheckIconWrapper>
              <FeatureText>
                {feature.text}
              </FeatureText>
            </FeatureRow>
          ))}
        </FeatureList>
        <OfferDivider />
        <CTARow>
          <PriceBlock>
            <strong>{getPromotionPlanPriceText(t, plan)}</strong>
            {plan.priceSubtext ? <span>{plan.priceSubtext}</span> : null}
          </PriceBlock>
          <CTAButton
            size="large"
            mode="dark"
            onClick={handleSubmit}
          >
            {plan.submitButtonText}
          </CTAButton>
        </CTARow>
      </OfferCardInner>
    </OfferCardRoot>
  );
};
